// ML001-F0218 / F0222 / F0223 (WebUI #169) and ML001-F0213 (apply / unapply). The Credit notes tag on the member Ledger page,
// coded to Spec core-ledger-account-gateway 1.10.0. Nav -> page -> tag -> drawer -> save: Active and Archived sections; Issue credit note
// creates a DRAFT; Approve & issue numbers it; Discard drops a draft; Cancel (issued notes) needs a reason; PDF is verified against
// pdf_sha256 before download. Apply to invoice (ML001-F0213) and Unapply (needs a reason) run through Core's generic module relay and
// need ledger_manage at SAL 4 (step-up); both are hidden on the viewer's own account (canPerformMoneyActions). History is the ONE shared
// TX history drawer, opened with the note's history_key (owner 2026-10-10: one history method).
// Write controls need ledger_manage; the whole tag is hidden without billing_transactions_view (in the page).
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { creditNoteApi } from '../../services/creditNoteApi';
import { creditAllocationApi, relayPayloadOf } from '../../services/creditAllocationApi';
import { ledgerAccountApi } from '../../services/api';
import StepUpDialog from '../auth/StepUpDialog';
import { getStepUpChallengeFromError } from '../../utils/stepUp';
import { useTxHistory } from '../txHistoryContext';
import {
  activeAllocationsFor,
  availableMinorTotal,
  buildApplyBody,
  buildDraftBody,
  canManageCreditNotes,
  emptyIssueForm,
  fromMinor,
  isDraft,
  invoiceOwedMinor,
  issuedInvoicesWithBalance,
  remainingMinor,
  rowId,
  splitCreditNotes,
  statusLabel,
  validateApplyForm,
  validateIssueForm,
  validateReason,
  validateRefundForm,
  buildRefundBody,
} from './creditNoteModel';
import { canPerformMoneyActions, isOwnAccount } from './moneyActionGate';
import { classifyRelayOutcome } from '../../services/relayOutcome';
import CreditPolicyCard from './CreditPolicyCard';
import { pdfBlobFromContract } from './creditNotePdf';
import ApplyToAccountDrawer from './ApplyToAccountDrawer';
import { canApplyToAccountRow } from './applyToAccountModel';
import './CreditNotesPanel.css';

const money = (minor) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(fromMinor(minor));
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})/;
// Dates arrive as YYYY-MM-DD calendar days: format by parts so no timezone shifts them.
export const formatDay = (value) => {
  const match = DATE_RE.exec(String(value || ''));
  return match ? `${match[3]}/${match[2]}/${match[1]}` : (value ? new Date(value).toLocaleDateString('en-GB') : '—');
};
const formatWhen = (value) => (value ? new Date(value).toLocaleString('en-GB') : '—');
const today = () => new Date().toISOString().slice(0, 10);
const newKey = () => globalThis.crypto?.randomUUID?.() || `cn-${Date.now()}-${Math.random()}`;
const apiError = (cause, fallback) => cause?.response?.data?.error?.message || cause?.response?.data?.message || cause?.message || fallback;
// Apply refusals the Ledger names in credit_allocation.apply (Spec core-ledger-account-gateway 1.11.0): each gets its own sentence,
// never the raw code; any other refusal keeps the node's own message.
const APPLY_REFUSAL_COPY = Object.freeze({
  LEDGER_INVOICE_NOT_FOUND: 'Invoice not found',
  LEDGER_INVOICE_NOT_CREDITABLE: "This invoice can't take credit",
});
const applyErrorText = (cause) => APPLY_REFUSAL_COPY[cause?.response?.data?.error?.code] || apiError(cause, 'The credit could not be applied.');
// M004-F0757 (WebUI #201): a Refund refusal shows the Ledger's code verbatim, then its message. Any other failure keeps the node's own message.
const refusalText = (cause, fallback) => {
  const error = cause?.response?.data?.error;
  return error?.code ? `${error.code}: ${error.message || fallback}` : apiError(cause, fallback);
};
// Features: M004-F0775
// Tests: T-M004-0379
// M004-F0775 (WebUI #248): an unknown outcome (no answer, a timeout, 502-504) means the Ledger may have applied the write. It is never a
// failure: the sentence names the thing, the view re-reads, and a keyed action keeps its Idempotency-Key for the next press.
const unconfirmed = (thing) => `We couldn't confirm whether ${thing}. We've refreshed what the Ledger shows — check it before trying again.`;
const rowsOf = (response) => {
  const data = response?.data?.data ?? response?.data ?? {};
  return Array.isArray(data.credit_notes) ? data.credit_notes : [];
};
// The Archived fact: why the Ledger archived the note (F0222). Shown as text, never as a raw code.
// Owner ruling (2026-10-09): final state is Used / Expired £X / Refunded / Cancelled. X is the balance the note still held when it expired.
const ARCHIVE_TEXT = Object.freeze({ used: 'Used', refunded: 'Refunded', cancelled: 'Cancelled' });
const expiredText = (row) => `Expired ${money(row.available_minor ?? row.amount_minor)}`;
const archiveText = (row) => {
  if (row.status === 'cancelled') return 'Cancelled';
  if (row.archive_reason === 'expired') return expiredText(row);
  return ARCHIVE_TEXT[row.archive_reason] || 'Archived';
};
// The status pill: an archived note shows its final state; everything else shows its status label.
const pillText = (row) => (statusLabel(row) === 'Expired' ? expiredText(row) : statusLabel(row));
const rowLabel = (row) => (isDraft(row) ? 'Draft credit note' : row?.credit_note_number);

function FieldError({ message, id }) {
  return message ? <small className="cn-field-error" id={id} role="alert">{message}</small> : null;
}

function IssueDrawer({ orgId, userId, customerName, onClose, onDrafted, onReload }) {
  const [form, setForm] = useState(() => emptyIssueForm(today()));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [formNotice, setFormNotice] = useState('');
  const idempotencyKey = useRef(newKey());
  const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const save = async (event) => {
    event.preventDefault();
    const found = validateIssueForm(form);
    setErrors(found);
    setFormError('');
    setFormNotice('');
    if (Object.keys(found).length) return;
    setSaving(true);
    try {
      const response = await creditNoteApi.createDraft(orgId, buildDraftBody(form, userId), idempotencyKey.current);
      onDrafted(response?.data?.data?.credit_note_draft);
    } catch (cause) {
      if (classifyRelayOutcome(cause) === 'unknown') {
        setFormNotice(unconfirmed('the draft was saved'));
        await onReload();
      } else {
        setFormError(apiError(cause, 'The draft could not be saved.'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="cn-drawer cn-drawer-form" role="dialog" aria-modal="false" aria-label="Issue credit note" data-testid="cn-issue-drawer" onSubmit={save} noValidate>
      <header className="cn-drawer-head"><h3>Issue credit note</h3><button type="button" onClick={onClose} aria-label="Close">×</button></header>
      <div className="cn-form">
        <p className="cn-muted">Customer: <strong>{customerName}</strong>. Saving creates a draft. It becomes a numbered credit note only when you approve it.</p>
        <label>Amount (£)<input type="number" min="0.01" step="0.01" data-testid="cn-amount" value={form.amount} onChange={set('amount')} aria-invalid={Boolean(errors.amount)} aria-describedby="cn-amount-err" /></label>
        <FieldError id="cn-amount-err" message={errors.amount} />
        <label>Reason<textarea data-testid="cn-reason" value={form.reason} onChange={set('reason')} aria-invalid={Boolean(errors.reason)} aria-describedby="cn-reason-err" /></label>
        <FieldError id="cn-reason-err" message={errors.reason} />
        <label>Issue date<input type="date" data-testid="cn-issue-date" value={form.issueDate} onChange={set('issueDate')} /></label>
        <FieldError message={errors.issueDate} />
        <label>Original invoice id (optional)<input value={form.originalInvoice} onChange={set('originalInvoice')} placeholder="Invoice id" /></label>
        {/* Owner ruling (2026-10-09): application is optional per credit. The draft contract (credit_note.draft) accepts no application
            fields yet, so none is sent here; the mode, discount, cap, expiry and charge types are set on the note once it is issued. */}
        <p className="cn-muted" data-testid="cn-issue-application">Application: Follow customer preference (the default). Choose Auto on invoice or Customer controlled on the note once it is issued.</p>
        {formError ? <p className="cn-form-error" role="alert">{formError}</p> : null}
        {formNotice ? <p className="cn-notice" role="status">{formNotice}</p> : null}
      </div>
      <footer className="cn-drawer-foot">
        <button type="button" onClick={onClose}>Cancel</button>
        <button type="submit" className="cn-primary" data-testid="cn-save-draft" disabled={saving}>{saving ? 'Saving…' : 'Save draft'}</button>
      </footer>
    </form>
  );
}

// Apply drawer (ML001-F0213): one of the member's ISSUED invoices that still owes money, an amount (at most the note's remaining balance
// and the invoice's balance due) and a reason. One Idempotency-Key per attempt: a step-up retry of the same body reuses it, a changed
// body starts a new attempt (the Ledger refuses a reused key with a different body).
function ApplyDrawer({ note, orgId, invoices, onGated, onApplied, onClose, onReload }) {
  const [form, setForm] = useState({ invoiceId: '', amount: '', reason: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [formNotice, setFormNotice] = useState('');
  const attempt = useRef(null);
  const set = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));
  const remaining = remainingMinor(note);
  const invoice = invoices.find((item) => item.invoice_id === form.invoiceId) || null;

  const save = async (event) => {
    event.preventDefault();
    const found = validateApplyForm(form, { remaining, invoiceDue: invoiceOwedMinor(invoice) });
    setErrors(found);
    setFormError('');
    setFormNotice('');
    if (Object.keys(found).length) return;
    const body = buildApplyBody({ creditNoteId: rowId(note), invoiceId: form.invoiceId, amount: form.amount, reason: form.reason });
    const fingerprint = JSON.stringify(body);
    if (!attempt.current || attempt.current.fingerprint !== fingerprint) attempt.current = { fingerprint, key: newKey() };
    const key = attempt.current.key;
    const attemptApply = async () => {
      await creditAllocationApi.apply(orgId, body, key);
      attempt.current = null;
      onApplied('Credit applied to the invoice.');
    };
    setSaving(true);
    try {
      await onGated(attemptApply);
    } catch (cause) {
      if (classifyRelayOutcome(cause) === 'unknown') {
        setFormNotice(unconfirmed('the credit was applied'));
        await onReload();
      } else {
        // A definite refusal ends the attempt: the next press sends a new key.
        if (classifyRelayOutcome(cause) === 'rejected') attempt.current = null;
        setFormError(applyErrorText(cause));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="cn-drawer cn-drawer-form" role="dialog" aria-modal="false" aria-label="Apply to invoice" data-testid="cn-apply-drawer" onSubmit={save} noValidate>
      <header className="cn-drawer-head"><h3>Apply to invoice</h3><button type="button" onClick={onClose} aria-label="Close">×</button></header>
      <div className="cn-form">
        <p className="cn-muted">Remaining on {rowLabel(note)}: <strong data-testid="cn-apply-remaining">{money(remaining)}</strong>. The invoice keeps its total; only what it owes goes down.</p>
        {invoices.length ? (
          <label>Invoice
            <select data-testid="cn-apply-invoice" value={form.invoiceId} onChange={set('invoiceId')} aria-invalid={Boolean(errors.invoice)}>
              <option value="">Choose an invoice</option>
              {invoices.map((item) => (
                <option key={item.invoice_id} value={item.invoice_id}>{item.invoice_number || item.invoice_id} · owes {money(invoiceOwedMinor(item))}</option>
              ))}
            </select>
          </label>
        ) : <p className="cn-empty" data-testid="cn-apply-no-invoices">No issued invoice with a balance to apply to.</p>}
        <FieldError message={errors.invoice} />
        <label>Amount (£)<input type="number" min="0.01" step="0.01" data-testid="cn-apply-amount" value={form.amount} onChange={set('amount')} aria-invalid={Boolean(errors.amount)} aria-describedby="cn-apply-amount-err" /></label>
        <FieldError id="cn-apply-amount-err" message={errors.amount} />
        <label>Reason<textarea data-testid="cn-apply-reason" value={form.reason} onChange={set('reason')} aria-invalid={Boolean(errors.reason)} aria-describedby="cn-apply-reason-err" /></label>
        <FieldError id="cn-apply-reason-err" message={errors.reason} />
        {formError ? <p className="cn-form-error" role="alert">{formError}</p> : null}
        {formNotice ? <p className="cn-notice" role="status">{formNotice}</p> : null}
      </div>
      <footer className="cn-drawer-foot">
        <button type="button" onClick={onClose}>Cancel</button>
        <button type="submit" className="cn-primary" data-testid="cn-apply-save" disabled={saving || !invoices.length}>{saving ? 'Applying…' : 'Apply credit'}</button>
      </footer>
    </form>
  );
}

// One applied allocation with its Unapply control (ML001-F0213). Unapply is a state flip, so it needs a reason.
function AllocationLine({ allocation, canApply, onUnapply }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(null);
  const submit = (event) => {
    event.preventDefault();
    const error = validateReason(reason, 'Give a reason for unapplying this credit.');
    setReasonError(error);
    if (error) return;
    onUnapply(allocation, reason.trim());
  };
  return (
    <li data-testid="cn-allocation" className="cn-allocation">
      <span>{allocation.invoice_number || allocation.invoice_id} · {money(allocation.amount_minor)} · {formatDay(allocation.date)}</span>
      {allocation.reason ? <span className="cn-muted"> · {allocation.reason}</span> : null}
      {canApply && !open ? <button type="button" className="cn-danger" data-testid="cn-unapply-open" onClick={() => setOpen(true)}>Unapply</button> : null}
      {canApply && open ? (
        <form onSubmit={submit} noValidate data-testid="cn-unapply-form" className="cn-form">
          <label>Reason for unapplying<textarea data-testid="cn-unapply-reason" value={reason} onChange={(event) => setReason(event.target.value)} aria-invalid={Boolean(reasonError)} aria-describedby="cn-unapply-err" /></label>
          <FieldError id="cn-unapply-err" message={reasonError} />
          <div className="cn-actions">
            <button type="button" onClick={() => { setOpen(false); setReasonError(null); }}>Back</button>
            <button type="submit" className="cn-danger" data-testid="cn-confirm-unapply">Unapply</button>
          </div>
        </form>
      ) : null}
    </li>
  );
}

function NoteDrawer({ row, orgId, userId, canManage, canApply, ownCustomer = true, allocations, allocationsError, onClose, onChanged, onReload, onHistory, onApply, onApplyToAccount, onGated, onUnapply }) {
  const [mode, setMode] = useState('view');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionNotice, setActionNotice] = useState('');
  // WebUI #249 (M004-F0775 §1.2): one key per attempt, held per action. The same action and body reuse the key.
  const attempts = useRef({});
  const refundAttempt = useRef(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundErrors, setRefundErrors] = useState({});
  const draft = isDraft(row);
  const issuedOpen = !draft && statusLabel(row) === 'Issued';
  // Admin only: ledger_manage (canManage) and never the viewer's own customer account (maker-checker, ML001-F0239).
  const canRefund = Boolean(canManage) && !ownCustomer && issuedOpen && !row.archived && !row.archive_reason && remainingMinor(row) > 0;
  const id = rowId(row);

  // An unknown outcome keeps the drawer open and the attempt (the same key stays for the next press with the same fingerprint) and re-reads the list; it is not a failure.
  const run = async (action, body, send, doneMessage, unknownThing) => {
    const fingerprint = JSON.stringify(body ?? null);
    if (attempts.current[action]?.fingerprint !== fingerprint) attempts.current[action] = { fingerprint, key: newKey() };
    setBusy(true); setActionError(''); setActionNotice('');
    try {
      await send(attempts.current[action].key);
      delete attempts.current[action];
      await onChanged(doneMessage);
    } catch (cause) {
      if (classifyRelayOutcome(cause) === 'unknown') {
        setActionNotice(unconfirmed(unknownThing));
        await onReload();
      } else {
        delete attempts.current[action];
        setActionError(apiError(cause, 'The change could not be saved.'));
      }
    } finally { setBusy(false); }
  };

  const approve = () => (!canManage || ownCustomer ? undefined : run('approve', null, (key) => creditNoteApi.approve(orgId, id, key, userId), 'Credit note issued.', 'the credit note was issued'));

  const cancel = (event) => {
    event.preventDefault();
    if (!canManage || ownCustomer) return;
    const error = validateReason(reason, 'Give a reason for cancelling this credit note.');
    setReasonError(error);
    if (error) return;
    const body = { reason: reason.trim() };
    run('cancel', body, (key) => creditNoteApi.cancel(orgId, id, body, key, userId), 'Credit note cancelled.', 'the credit note was cancelled');
  };

  // M004-F0757 (WebUI #201): Refund CR. Admin only (ledger_manage and not the viewer's own account); the amount is capped at what the note
  // still holds and a comment is required. One Idempotency-Key per attempt: the same body reuses it, a changed body starts a new attempt.
  const refund = async (event) => {
    event.preventDefault();
    if (!canRefund) return;
    const found = validateRefundForm({ amount: refundAmount, reason }, { available: remainingMinor(row) });
    setRefundErrors(found);
    if (Object.keys(found).length) return;
    const body = buildRefundBody({ customerId: row.user_id, amount: refundAmount, reason });
    const fingerprint = JSON.stringify(body);
    if (!refundAttempt.current || refundAttempt.current.fingerprint !== fingerprint) refundAttempt.current = { fingerprint, key: newKey() };
    setBusy(true); setActionError(''); setActionNotice('');
    try {
      await creditAllocationApi.refund(orgId, body, refundAttempt.current.key);
      refundAttempt.current = null;
      setMode('view'); setRefundAmount(''); setReason('');
      await onChanged('Refund recorded.');
    } catch (cause) {
      if (classifyRelayOutcome(cause) === 'unknown') {
        // The key stays on refundAttempt: the next press with the same comment and amount reuses it.
        setActionNotice(unconfirmed('the refund was recorded'));
        await onReload();
      } else {
        // A definite refusal ends the attempt: the next press sends a new key.
        refundAttempt.current = null;
        setActionError(refusalText(cause, 'The refund could not be recorded.'));
      }
    } finally { setBusy(false); }
  };

  const discard = (event) => {
    event.preventDefault();
    const text = reason.trim();
    const body = text ? { reason: text } : {};
    run('discard', body, (key) => creditNoteApi.discardDraft(orgId, id, body, key, userId), 'Draft discarded.', 'the draft was discarded');
  };

  const downloadPdf = async () => {
    setActionError('');
    try {
      const response = await creditNoteApi.pdf(orgId, id);
      const data = response.data.data;
      const url = URL.createObjectURL(await pdfBlobFromContract(data));
      const link = document.createElement('a');
      link.href = url;
      link.download = data.filename;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (cause) {
      setActionError(apiError(cause, 'The PDF could not be downloaded.'));
    }
  };

  const applied = draft ? [] : (allocations || []);
  return (
    <aside className="cn-drawer" role="dialog" aria-modal="false" aria-label="Credit note" data-testid="cn-note-drawer">
      <header className="cn-drawer-head"><h3>{rowLabel(row) || 'Credit note'}</h3><button type="button" onClick={onClose} aria-label="Close">×</button></header>
      <dl className="cn-facts" data-testid="cn-note-facts">
        <div><dt>Status</dt><dd data-testid="cn-note-status">{pillText(row)}</dd></div>
        <div><dt>Customer</dt><dd>{row.customer_name || '—'}</dd></div>
        <div><dt>Amount</dt><dd>{money(row.amount_minor)}</dd></div>
        <div><dt>Reason</dt><dd>{row.reason || '—'}</dd></div>
        <div><dt>Date</dt><dd>{formatDay(row.issue_date)}</dd></div>
        <div><dt>Expires</dt><dd data-testid="cn-note-expires">{row.expires_on ? formatDay(row.expires_on) : 'No expiry date'}</dd></div>
        <div><dt>Archived</dt><dd data-testid="cn-note-archived">{row.archived || row.archive_reason ? `${archiveText(row)}${row.archived_at ? `, ${formatDay(row.archived_at)}` : ''}` : 'Active'}</dd></div>
        <div><dt>Original invoice</dt><dd>{row.original_invoice_number || row.original_invoice_id || '—'}</dd></div>
        {!draft ? <div><dt>Remaining</dt><dd data-testid="cn-note-remaining">{money(row.available_minor)}</dd></div> : null}
      </dl>
      <p className="cn-muted">Amount, reason and date are locked once a note is issued.</p>
      {actionError ? <p className="cn-form-error" role="alert">{actionError}</p> : null}
      {actionNotice ? <p className="cn-notice" role="status">{actionNotice}</p> : null}
      {issuedOpen && !row.archived && !row.archive_reason ? (
        <CreditPolicyCard level="credit" title="Application for this credit note" orgId={orgId} userId={row.user_id} creditNoteId={id} canManage={canManage} ownAccount={ownCustomer} />
      ) : null}
      {mode === 'refund' && canRefund ? (
        <form className="cn-form" onSubmit={refund} noValidate data-testid="cn-refund-form">
          <label>Refund amount (£)<input type="number" min="0.01" step="0.01" data-testid="cn-refund-amount" value={refundAmount} onChange={(event) => setRefundAmount(event.target.value)} aria-invalid={Boolean(refundErrors.amount)} aria-describedby="cn-refund-amount-err" /></label>
          <small className="cn-muted">At most {money(remainingMinor(row))}, what this note still holds.</small>
          <FieldError id="cn-refund-amount-err" message={refundErrors.amount} />
          <label>Comment<textarea data-testid="cn-refund-reason" value={reason} onChange={(event) => setReason(event.target.value)} aria-invalid={Boolean(refundErrors.reason)} aria-describedby="cn-refund-reason-err" /></label>
          <FieldError id="cn-refund-reason-err" message={refundErrors.reason} />
          <div className="cn-actions">
            <button type="button" onClick={() => { setMode('view'); setRefundErrors({}); }}>Back</button>
            <button type="submit" className="cn-danger" data-testid="cn-confirm-refund" disabled={busy}>Refund</button>
          </div>
        </form>
      ) : mode === 'cancel' ? (
        <form className="cn-form" onSubmit={cancel} noValidate data-testid="cn-cancel-form">
          <label>Reason for cancelling<textarea data-testid="cn-cancel-reason" value={reason} onChange={(event) => setReason(event.target.value)} aria-invalid={Boolean(reasonError)} aria-describedby="cn-cancel-err" /></label>
          <FieldError id="cn-cancel-err" message={reasonError} />
          <div className="cn-actions">
            <button type="button" onClick={() => { setMode('view'); setReasonError(null); }}>Back</button>
            <button type="submit" className="cn-danger" data-testid="cn-confirm-cancel" disabled={busy}>Cancel credit note</button>
          </div>
        </form>
      ) : mode === 'discard' ? (
        <form className="cn-form" onSubmit={discard} noValidate data-testid="cn-discard-form">
          <label>Reason (optional)<textarea data-testid="cn-discard-reason" value={reason} onChange={(event) => setReason(event.target.value)} /></label>
          <div className="cn-actions">
            <button type="button" onClick={() => setMode('view')}>Back</button>
            <button type="submit" className="cn-danger" data-testid="cn-confirm-discard" disabled={busy}>Discard draft</button>
          </div>
        </form>
      ) : (
        <div className="cn-actions">
          {canManage && !ownCustomer && draft ? <button type="button" className="cn-primary" data-testid="cn-approve" onClick={approve} disabled={busy}>Approve &amp; issue</button> : null}
          {canManage && draft ? <button type="button" className="cn-danger" data-testid="cn-discard-open" onClick={() => setMode('discard')}>Discard draft</button> : null}
          {canManage && !ownCustomer && issuedOpen ? <button type="button" className="cn-danger" data-testid="cn-cancel-open" onClick={() => setMode('cancel')}>Cancel credit note</button> : null}
          {canManage && ownCustomer && (draft || issuedOpen) ? <span className="cn-muted" data-testid="cn-maker-checker-hint">Another billing manager must approve</span> : null}
          {canApply && !draft && !row.archived && !row.archive_reason && remainingMinor(row) > 0 ? <button type="button" className="cn-primary" data-testid="cn-apply-open" onClick={onApply}>Apply to invoice</button> : null}
          {canManage && canApplyToAccountRow(row) ? <button type="button" className="cn-primary" data-testid="cn-account-open" onClick={onApplyToAccount}>Apply to account</button> : null}
          {canRefund ? <button type="button" className="cn-danger" data-testid="cn-refund-open" onClick={() => { setMode('refund'); setRefundErrors({}); }}>Refund</button> : null}
          {!draft ? <button type="button" data-testid="cn-pdf" onClick={downloadPdf}>Download PDF</button> : null}
          {!draft && row.history_key ? <button type="button" data-testid="cn-history-open" onClick={() => onHistory(row)}>History</button> : null}
          {!draft && !row.history_key ? <span className="cn-muted" data-testid="cn-history-unavailable">History not available yet</span> : null}
        </div>
      )}
      {!draft ? (
        <section className="cn-applied" data-testid="cn-applied-section" aria-label="Applied to">
          <h4>Applied to</h4>
          {allocationsError ? <p className="cn-form-error" role="alert" data-testid="cn-applied-error">{allocationsError}</p> : null}
          {!allocationsError && !applied.length ? <p className="cn-muted" data-testid="cn-applied-empty">Not applied to any invoice.</p> : null}
          {applied.length ? (
            <ul className="cn-allocations" data-testid="cn-applied-list">
              {applied.map((allocation) => (
                <AllocationLine key={allocation.allocation_id} allocation={allocation} canApply={canApply} onUnapply={(item, text) => onUnapply(item, text)} />
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}
    </aside>
  );
}

// ML001-F0239 (maker-checker): write controls need ledger_manage. A manager may DRAFT (and discard a draft) for themselves, but
// may not APPROVE or CANCEL a note whose customer is the viewer: another billing manager must (hint shown, no control rendered).
// The pinned wire carries no created_by, so "customer is the viewer" is the test (a superset of "viewer is the drafter").
// ML001-F0213: Apply and Unapply follow canPerformMoneyActions (ledger_manage AND not the viewer's own account): a control that is not
// allowed is not rendered.
export default function CreditNotesPanel({ orgId, userId, customerName, permissions, viewerId }) {
  const canManage = canManageCreditNotes(permissions);
  const ownCustomer = isOwnAccount(viewerId, userId);
  const canApply = canPerformMoneyActions({ permissions, viewerId, targetUserId: userId });
  const history = useTxHistory();
  const [rows, setRows] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [allocationsError, setAllocationsError] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [section, setSection] = useState('active');
  const [drawer, setDrawer] = useState(null);
  const [notice, setNotice] = useState('');
  const [panelError, setPanelError] = useState('');
  const [stepUp, setStepUp] = useState(null);
  const retry = useRef(null);

  const load = useCallback(async () => {
    setLoadError('');
    try {
      setRows(rowsOf(await creditNoteApi.list(orgId, { user_id: Number(userId) })));
    } catch (cause) {
      setLoadError(apiError(cause, 'Credit notes could not be loaded.'));
      setRows([]);
    }
    setAllocationsError('');
    try {
      const payload = relayPayloadOf(await creditAllocationApi.list(orgId, { user_id: Number(userId) }));
      setAllocations(Array.isArray(payload?.data?.credit_allocations) ? payload.data.credit_allocations : []);
    } catch (cause) {
      setAllocations([]);
      setAllocationsError(apiError(cause, 'Applied credit could not be loaded.'));
    }
  }, [orgId, userId]);

  useEffect(() => { load(); }, [load]);

  // Invoices for the apply picker come from the member's account read (the member's invoices; see issuedInvoicesWithBalance).
  useEffect(() => {
    if (!canApply) return undefined;
    let current = true;
    ledgerAccountApi.getAccount(orgId, userId)
      .then((response) => { if (current) setInvoices(issuedInvoicesWithBalance(response?.data?.data?.invoices)); })
      .catch(() => { if (current) setInvoices([]); });
    return () => { current = false; };
  }, [canApply, orgId, userId]);

  // A write that is refused for step-up opens the platform's StepUpDialog; on completion the SAME action runs again (same key).
  const gated = useCallback(async (action) => {
    try {
      await action();
    } catch (cause) {
      const challenge = getStepUpChallengeFromError(cause);
      if (!challenge?.id) throw cause;
      retry.current = action;
      setStepUp(challenge);
    }
  }, []);

  const finishStepUp = async () => {
    const action = retry.current;
    retry.current = null;
    setStepUp(null);
    if (!action) return;
    try {
      await action();
    } catch (cause) {
      if (classifyRelayOutcome(cause) === 'unknown') setNotice(unconfirmed('the change went through'));
      else setPanelError(apiError(cause, 'The change could not be saved.'));
    }
    await load();
  };

  const { active, archived } = splitCreditNotes(rows || []);
  const visible = section === 'active' ? active : archived;
  const drawerRow = drawer?.id ? (rows || []).find((row) => rowId(row) === drawer.id) : null;
  const allocationsFor = (note) => activeAllocationsFor(allocations, rowId(note));

  const afterChange = async (message) => {
    setNotice(message);
    setPanelError('');
    setDrawer(null);
    await load();
  };

  const unapply = async (allocation, text) => {
    setPanelError('');
    const body = { allocation_id: allocation.allocation_id, reason: text };
    try {
      await gated(async () => {
        await creditAllocationApi.unapply(orgId, body);
        await afterChange('Credit unapplied from the invoice.');
      });
    } catch (cause) {
      if (classifyRelayOutcome(cause) === 'unknown') {
        setNotice(unconfirmed('the credit was unapplied'));
        await load();
      } else {
        setPanelError(apiError(cause, 'The credit could not be unapplied.'));
      }
    }
  };

  const openHistory = (row) => {
    setDrawer(null);
    history?.openTransaction({ key: row.history_key, orgId, customerUserId: userId });
  };

  return (
    <section className="cn-panel" data-testid="credit-notes-panel" aria-label="Credit notes">
      <StepUpDialog
        challenge={stepUp}
        title="Verify credit change"
        description="Confirm your identity before changing this member's credit."
        onComplete={finishStepUp}
        onCancel={() => { retry.current = null; setStepUp(null); setPanelError('Verification was cancelled.'); }}
      />
      <CreditPolicyCard level="customer" title="Credit application for this customer" orgId={orgId} userId={userId} canManage={canManage} ownAccount={ownCustomer} />
      <header className="cn-panel-head">
        <div>
          <h3>Credit notes</h3>
          <p className="cn-muted">Each credit note is its own numbered document. Applying it to invoices is recorded separately.</p>
        </div>
        <div className="cn-panel-meta">
          <span className="cn-chip" data-testid="cn-available">Available {money(availableMinorTotal(active))}</span>
          {canManage ? <button type="button" className="cn-primary" data-testid="cn-issue-open" onClick={() => setDrawer({ kind: 'issue' })}>Issue credit note</button> : null}
        </div>
      </header>
      {notice ? <p className="cn-notice" role="status">{notice}</p> : null}
      {panelError ? <p className="cn-form-error" role="alert" data-testid="cn-panel-error">{panelError}</p> : null}
      {loadError ? <p className="cn-form-error" role="alert">{loadError}</p> : null}

      <div className="cn-subtabs" role="tablist" aria-label="Credit note sections">
        <button type="button" role="tab" aria-selected={section === 'active'} className={section === 'active' ? 'active' : ''} data-testid="cn-tab-active" onClick={() => setSection('active')}>Active ({active.length})</button>
        <button type="button" role="tab" aria-selected={section === 'archived'} className={section === 'archived' ? 'active' : ''} data-testid="cn-tab-archived" onClick={() => setSection('archived')}>Archived ({archived.length})</button>
      </div>

      {rows === null ? <p role="status">Loading credit notes…</p> : null}
      {rows !== null && !visible.length ? (
        <p className="cn-empty" data-testid="cn-empty">{section === 'active' ? 'No active credit notes.' : 'No archived credit notes.'}</p>
      ) : null}
      {visible.length ? (
        <table className="cn-table" data-testid={`cn-table-${section}`}>
          <thead>
            <tr>
              <th>Number</th><th>Date</th><th>Amount</th><th>Available</th><th>Status</th>
              {section === 'archived' ? <th>Archived</th> : null}
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={rowId(row)} data-testid={`cn-row-${rowId(row)}`} onClick={() => setDrawer({ kind: 'note', id: rowId(row) })}>
                <td><button type="button" className="cn-link" onClick={(event) => { event.stopPropagation(); setDrawer({ kind: 'note', id: rowId(row) }); }}>{rowLabel(row) || 'Draft'}</button></td>
                <td>{formatDay(row.issue_date)}</td>
                <td>{money(row.amount_minor)}</td>
                <td>{section === 'archived' || isDraft(row) ? '—' : money(row.available_minor)}</td>
                <td><span className={`cn-pill cn-pill-${String(statusLabel(row)).toLowerCase()}`} data-testid="cn-status-pill">{pillText(row)}</span></td>
                {section === 'archived' ? <td>{formatDay(row.archived_at)}</td> : null}
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {drawer?.kind === 'issue' && canManage ? (
        <IssueDrawer
          orgId={orgId}
          userId={userId}
          customerName={customerName}
          onClose={() => setDrawer(null)}
          onDrafted={(draft) => { setNotice('Draft saved. Approve & issue to number it.'); setDrawer({ kind: 'note', id: draft?.credit_note_draft_id }); load(); }}
          onReload={load}
        />
      ) : null}
      {drawer?.kind === 'note' && drawerRow ? (
        <NoteDrawer
          row={drawerRow}
          orgId={orgId}
          userId={userId}
          canManage={canManage}
          canApply={canApply}
          ownCustomer={ownCustomer}
          allocations={allocationsFor(drawerRow)}
          allocationsError={allocationsError}
          onClose={() => setDrawer(null)}
          onChanged={afterChange}
          onReload={load}
          onHistory={openHistory}
          onApply={() => setDrawer({ kind: 'apply', id: rowId(drawerRow) })}
          onApplyToAccount={() => setDrawer({ kind: 'account', id: rowId(drawerRow) })}
          onGated={gated}
          onUnapply={unapply}
        />
      ) : null}
      {drawer?.kind === 'account' && drawerRow && canManage ? (
        <ApplyToAccountDrawer
          note={drawerRow}
          orgId={orgId}
          onApplied={(done) => afterChange(`${done.message}. ${done.figures}`)}
          onUnconfirmed={load}
          onClose={() => setDrawer({ kind: 'note', id: drawer.id })}
        />
      ) : null}
      {drawer?.kind === 'apply' && drawerRow && canApply ? (
        <ApplyDrawer
          note={drawerRow}
          orgId={orgId}
          invoices={invoices}
          onGated={gated}
          onApplied={(message) => afterChange(message)}
          onReload={load}
          onClose={() => setDrawer({ kind: 'note', id: drawer.id })}
        />
      ) : null}
    </section>
  );
}
